import React, { useState, useEffect } from 'react';
import { Pencil, Trash2, Plus, MapPin, Users, Maximize } from 'lucide-react';
import { getAllLocations, deleteLocation } from '../../../services/locations';
import LoadingSpinner from '../../shared/LoadingSpinner';
import './LocationList.css';

const LocationList = ({ onEdit, onAdd }) => {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteConfirm, setDeleteConfirm] = useState(null);

  useEffect(() => {
    loadLocations();
  }, []);

  const loadLocations = async () => {
    setLoading(true);
    const result = await getAllLocations();
    if (result.success) {
      setLocations(result.data);
    }
    setLoading(false);
  };

  const handleDelete = async (id) => {
    const result = await deleteLocation(id);
    if (result.success) {
      setLocations(locations.filter(loc => loc.id !== id));
      setDeleteConfirm(null);
    } else {
      alert('Error deleting location: ' + result.error);
    }
  };

  if (loading) {
    return <LoadingSpinner />;
  }

  if (locations.length === 0) {
    return (
      <div className="admin-empty-state">
        <div className="admin-empty-icon">
          <MapPin size={64} />
        </div>
        <h3 className="admin-empty-title">No Locations Yet</h3>
        <p className="admin-empty-text">
          Get started by adding your first filming location.
        </p>
        <button onClick={onAdd} className="btn btn-primary">
          <Plus size={20} />
          Add First Location
        </button>
      </div>
    );
  }

  return (
    <div className="location-list">
      <div className="admin-cards-grid">
        {locations.map((location) => (
          <div key={location.id} className="admin-item-card">
            <div className="admin-card-image">
              {location.images && location.images.length > 0 ? (
                <img src={location.images[0]} alt={location.name} />
              ) : (
                <div className="admin-card-placeholder">
                  <MapPin size={32} />
                </div>
              )}
            </div>
            <div className="admin-card-content">
              <h3 className="admin-card-title">{location.name}</h3>
              <p className="admin-card-subtitle">{location.address}</p>
              <div className="admin-card-meta">
                <span className="meta-item">
                  <Maximize size={14} />
                  {location.size}
                </span>
                <span className="meta-item">
                  <Users size={14} />
                  {location.capacity}
                </span>
              </div>
              <div className="admin-card-footer">
                <span className="admin-card-price">${location.price}/day</span>
                <div className="admin-card-actions">
                  <button
                    onClick={() => onEdit(location)}
                    className="btn btn-ghost btn-sm"
                    title="Edit"
                  >
                    <Pencil size={16} />
                  </button>
                  <button
                    onClick={() => setDeleteConfirm(location.id)}
                    className="btn btn-danger btn-sm"
                    title="Delete"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="modal-overlay" onClick={() => setDeleteConfirm(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3 className="modal-title">Confirm Delete</h3>
            <p className="modal-text">
              Are you sure you want to delete this location? This action cannot be undone.
            </p>
            <div className="modal-actions">
              <button
                onClick={() => setDeleteConfirm(null)}
                className="btn btn-ghost"
              >
                Cancel
              </button>
              <button
                onClick={() => handleDelete(deleteConfirm)}
                className="btn btn-danger"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LocationList;
